class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  lockScroll() {
    if (document.body.classList.contains('cart-drawer-overflow-hidden')) return
    const scrollY = window.scrollY;
    document.body.style.top = `-${scrollY}px`;
    document.body.classList.add('cart-drawer-overflow-hidden');
  }

  unlockScroll() {
    const scrollY = document.body.style.top;
    document.body.classList.remove('cart-drawer-overflow-hidden');
    document.body.style.top = '';
    window.scrollTo({
      top: parseInt(scrollY || '0') * -1,
      behavior: 'instant'
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    this.lockScroll();

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    this.unlockScroll();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

if (customElements && !customElements.get('cta-enable-subscription')) {
  class CTAEnableSubscription extends HTMLElement {
    constructor() {
      super();

      this.key = this.dataset.key;
      this.quantity = this.dataset.quantity;
      this.variantId = this.dataset.variantId;
      this.sellingPlanId = this.dataset.sellingPlanId;
    }

    connectedCallback() {
      this.addEventListener('keydown', async (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          await this.render();
        }
      });

      this.addEventListener('click', async (event) => {
        event.preventDefault();
        await this.render();
      });
    }

    lockButtonToCheckout() {
      const cartDrawerBtnToCheckout = document.querySelector('cart-drawer #CartDrawer-Checkout');
      if (!cartDrawerBtnToCheckout) return;

      cartDrawerBtnToCheckout.setAttribute('disabled', '');
    }

    async addToCart(productId = 0, quantity = 1, sellingPlan = '') {
      try {
        const formData = {
          items: [{
            'id': productId,
            'quantity': quantity,
            'selling_plan': sellingPlan
          }]
        };

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });

        if (!response.ok) throw new Error(`Could not add gift products, status: ${response.status}`);

        const data = await response.json();
        return data;
      } catch (err) {
        console.error('Error adding product products:', err);
      }
    }

    async updateQuantity(productId = 0, quantity = 1) {
      try {
        const updates = {
          [productId]: quantity
        };

        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ updates })
        });

        if (!response.ok) throw new Error(`Could not update gift quantity, status: ${response.status}`);

        const data = await response.json();

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'gift-sync',
          cartData: data
        });

        return data;
      } catch (err) {
        console.error('Error updating gift quantity:', err);
      }
    }

    async render() {
      this.lockButtonToCheckout();
      await this.updateQuantity(this.key, 0);
      await this.addToCart(this.variantId, this.quantity, this.sellingPlanId);
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cta-enable-subscription'
      });
    }
  }

  customElements.define('cta-enable-subscription', CTAEnableSubscription);
}